import { useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import Group from 'components/utils/Group'
import LayoutContainer from 'components/Common/LayoutContainer'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Heading from 'LuxKit/Typography/Heading'
import AssistChip from 'LuxKit/Chips/AssistChip'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import { useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import { useLocation } from 'react-router'
import ModalContext from 'contexts/ModalContext'
import VoucherDetailsModal from 'components/Pages/VouchersPage/VoucherDetailsModal'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { VoucherDisplayConfig as VoucherDisplayConfigType, VoucherDisplayConfigVoucher } from 'api/promo'
import {
  getVoucherDisplayConfigs,
  getClaimedVoucherDisplayConfigVouchers,
  claimVoucherDisplayConfig,
} from 'api/promo'
import { trackEvent } from 'analytics/analytics'
import { promotionClickEvent, promotionViewEvent } from 'analytics/snowplow/events'
import VoucherCarouselCard from './VoucherCarouselCard'

const VOUCHER_DISPLAY_PROMOTION_TYPE = 'banner'
const VOUCHER_CLAIM_CLICK_ACTION = 'claim'

function matchUrlPath(pattern: string, pathname: string): boolean {
  if (pattern.endsWith('/*')) {
    return pathname.startsWith(pattern.slice(0, -1))
  }
  return pathname === pattern
}

function VoucherDisplayConfig() {
  const isEnabled = config.ENABLE_VOUCHER_DISPLAY_CONFIG
  const intl = useIntl()
  const queryClient = useQueryClient()
  const showModal = useContext(ModalContext)
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const isLoggedIn = !!useAppSelector(state => state.auth.account.memberId)
  const { pathname } = useLocation()
  const pathnameWithoutRegion = pathname.replace(/^\/[a-z]{2}\//i, '/')
  const utmSource = useAppSelector(state => state.utm.source)
  const utmMedium = useAppSelector(state => state.utm.medium)
  const utmCampaign = useAppSelector(state => state.utm.campaign)

  const { data: configs = [] } = useQuery({
    queryKey: ['voucher-display-configs', region],
    queryFn: () => getVoucherDisplayConfigs({ region }),
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000,
  })

  const { data: claimedVoucherIds = [] } = useQuery({
    queryKey: ['claimed-voucher-display-config-vouchers', region],
    queryFn: () => getClaimedVoucherDisplayConfigVouchers({ region }),
    enabled: isEnabled && isLoggedIn,
    staleTime: 5 * 60 * 1000,
  })

  const voucherAddedMessage = intl.formatMessage({
    id: 'bkRqGhY9sOsDJI6L2dU4',
    defaultMessage: 'Voucher added to your account',
    description: 'Success snackbar message shown after a voucher is claimed in the available rewards section',
  })

  const viewLabel = intl.formatMessage({
    id: 'Eyke30bZXtdgg_N6kLLw',
    defaultMessage: 'View',
    description: 'Action label on the success snackbar to navigate to the vouchers page after claiming a voucher',
  })

  const claimMutation = useMutation({
    mutationFn: ({ voucherDisplayConfigId, voucherId }: { voucherDisplayConfigId: string; voucherId: string }) =>
      claimVoucherDisplayConfig(voucherDisplayConfigId, voucherId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['claimed-voucher-display-config-vouchers'] })
      showSnackbar(voucherAddedMessage, 'success', {
        icon: <SolidCheckCircleIcon />,
        action: { label: viewLabel, to: '/account/vouchers' },
      })
    },
  })

  const handleClaim = useCallback((voucherDisplayConfigId: string, voucher: VoucherDisplayConfigVoucher) => {
    trackEvent(promotionClickEvent({
      promotionContext: { promotionType: VOUCHER_DISPLAY_PROMOTION_TYPE, promotionName: voucherDisplayConfigId },
      voucherContexts: [{ voucherId: voucher.voucherId, voucherName: voucher.voucherName }],
      clickAction: VOUCHER_CLAIM_CLICK_ACTION,
    }))
    if (!isLoggedIn) {
      showModal(<AccountAccessModal />)
      return
    }
    claimMutation.mutate({ voucherDisplayConfigId, voucherId: voucher.voucherId })
  }, [isLoggedIn, showModal, claimMutation])

  const handleDetailsClick = useCallback((voucher: VoucherDisplayConfigVoucher) => {
    showModal(<VoucherDetailsModal title={voucher.voucherTitle} description={voucher.voucherDescription} />)
  }, [showModal])

  const matchedConfig = useMemo(() => configs.find((cfg: VoucherDisplayConfigType) => {
    const urlMatch = cfg.urlPaths.some((pattern: string) => matchUrlPath(pattern, pathnameWithoutRegion))
    if (!urlMatch) return false
    if (cfg.source && cfg.source !== utmSource) return false
    if (cfg.medium && cfg.medium !== utmMedium) return false
    if (cfg.campaign && cfg.campaign !== utmCampaign) return false
    return true
  }), [configs, pathnameWithoutRegion, utmSource, utmMedium, utmCampaign])

  const allVouchers: Array<{ config: VoucherDisplayConfigType; voucher: VoucherDisplayConfigVoucher }> = matchedConfig
    ? matchedConfig.vouchers.map((voucher: VoucherDisplayConfigVoucher) => ({ config: matchedConfig, voucher }))
    : []

  const matchedConfigId = matchedConfig?.voucherDisplayConfigId
  useEffect(() => {
    if (!matchedConfig || matchedConfig.vouchers.length === 0) return
    trackEvent(promotionViewEvent({
      promotionContext: { promotionType: VOUCHER_DISPLAY_PROMOTION_TYPE, promotionName: matchedConfig.voucherDisplayConfigId },
      voucherContexts: matchedConfig.vouchers.map((voucher) => ({
        voucherId: voucher.voucherId,
        voucherName: voucher.voucherName,
      })),
    }))
  }, [matchedConfigId, matchedConfig])

  const claimLabel = intl.formatMessage({
    id: 'zfbRgZ-tl2pnCiCVfvu2',
    defaultMessage: 'Claim',
    description: 'Button label to claim a reward voucher in the available rewards section',
  })

  const expiryText = useMemo(() => {
    if (!matchedConfig) return undefined
    if (matchedConfig.voucherExpiryDays) {
      return intl.formatMessage(
        {
          id: 'ud8LXfTQEwYf6MPk0SEy',
          defaultMessage: 'Expires in {count, plural, one {# day} other {# days}}',
          description: 'Expiry days countdown text shown on voucher cards in the available rewards section',
        },
        { count: matchedConfig.voucherExpiryDays },
      )
    }
    if (matchedConfig.voucherExpiryDate) {
      return intl.formatMessage(
        {
          id: 'xo490XgN_oxAUFu5Tggh',
          defaultMessage: 'Expires {expiryDate}',
          description: 'Expiry date text shown on voucher cards in the available rewards section',
        },
        {
          expiryDate: intl.formatDate(new Date(matchedConfig.voucherExpiryDate), {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          }),
        },
      )
    }
    return undefined
  }, [matchedConfig, intl])

  if (allVouchers.length === 0) {
    return null
  }

  return (
    <LayoutContainer size="xl" style={{ paddingTop: rem(40), paddingBottom: rem(24) }}>
      <Group direction="vertical" gap={20}>
        <Heading variant="heading2" as="h2">
          <FormattedMessage
            id="W2ep2lxHH_jAirqkQBi8"
            defaultMessage="Available <italic>rewards</italic>"
            description="Section heading for available reward vouchers on the metasearch page"
            values={{
              italic: (chunks) => <em key="rewards-italic">{chunks}</em>,
            }}
          />
        </Heading>

        <CSSBreakpoint max="mobile">
          <HorizontalContainerWithFadedOverflow gap={12}>
            {allVouchers.map(({ config: cfg, voucher }) => {
              const isClaimed = claimedVoucherIds.includes(voucher.voucherId)
              let rightSlot: ReactNode
              if (isClaimed) {
                rightSlot = <LineAngleRightIcon size="L" colour="light-foreground-alt" />
              } else {
                rightSlot = (
                  <AssistChip
                    variant="filled"
                    size="small"
                    onClick={() => handleClaim(cfg.voucherDisplayConfigId, voucher)}
                    disabled={claimMutation.isPending}
                  >
                    {claimLabel}
                  </AssistChip>
                )
              }
              return (
                <VoucherCarouselCard
                  key={voucher.voucherDisplayConfigVoucherId}
                  variant="mobile"
                  voucher={voucher}
                  expiryText={expiryText}
                  rightSlot={rightSlot}
                  onDetailsClick={voucher.voucherDescription ? () => handleDetailsClick(voucher) : undefined}
                />
              )
            })}
          </HorizontalContainerWithFadedOverflow>
        </CSSBreakpoint>

        <CSSBreakpoint min="tablet">
          <HorizontalContainerWithFadedOverflow gap={20}>
            {allVouchers.map(({ config: cfg, voucher }) => {
              const isClaimed = claimedVoucherIds.includes(voucher.voucherId)
              let rightSlot: ReactNode
              if (isClaimed) {
                rightSlot = <LineAngleRightIcon size="L" colour="light-foreground-alt" />
              } else {
                rightSlot = (
                  <AssistChip
                    variant="filled"
                    size="medium"
                    onClick={() => handleClaim(cfg.voucherDisplayConfigId, voucher)}
                    disabled={claimMutation.isPending}
                  >
                    {claimLabel}
                  </AssistChip>
                )
              }
              return (
                <VoucherCarouselCard
                  key={voucher.voucherDisplayConfigVoucherId}
                  variant="desktop"
                  voucher={voucher}
                  expiryText={expiryText}
                  rightSlot={rightSlot}
                  onDetailsClick={voucher.voucherDescription ? () => handleDetailsClick(voucher) : undefined}
                />
              )
            })}
          </HorizontalContainerWithFadedOverflow>
        </CSSBreakpoint>
      </Group>
    </LayoutContainer>
  )
}

export default VoucherDisplayConfig
